import { ContractModel, CustomFieldValue } from "../../contract/Contract.types.ts";
import { ContractStandardField, EntityTemplateField } from "../../../entityTemplates/EntityTemplate.types.ts";
import {
    ContractExtractionResponse,
    EditableContractParty,
    ExtractedContractParty,
    ExtractedCustomField,
    ExtractedStandardField,
} from "./CreateContractPane.types.ts";
import { applyCustomFieldValue } from "../../../entityTemplates/fields/customFields/customField.hooks.ts";
import { getReviewableStandardFields } from "../../contractAiUpdate/reviewableTemplateFields.ts";
import { ContractStatus } from "../../Contracts.types.ts";
import { InputFieldTypeEnum } from "../../../entityTemplates/inputFields/InputFields.types.ts";
import { exhaustiveGuard } from "../../../common/utilities.ts";

// new Date("2024-04-01") is UTC midnight, which west of UTC is the previous local day. Local noon is what
// DotLegalDatePicker itself stores, so the date shows, compares and saves as the day the document states.
function parseDate(value: string): Date | undefined {
    if (!value) return undefined;
    const isoDate = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (isoDate) {
        return new Date(Number(isoDate[1]), Number(isoDate[2]) - 1, Number(isoDate[3]), 12);
    }
    const parsed = new Date(value);
    return isNaN(parsed.getTime()) ? undefined : parsed;
}

function parseNumber(value: string): number | undefined {
    if (!value) return undefined;
    const parsed = parseFloat(value.replace(/[^\d.,-]/g, "").replace(",", "."));
    return isNaN(parsed) ? undefined : parsed;
}

function parseBoolean(value: string): boolean {
    if (!value) return false;
    const lower = value.toLowerCase();
    return lower === "true" || lower === "yes" || lower === "1" || lower === "ja";
}

export function mapStandardFieldToContractModel(field: ExtractedStandardField, contract: Partial<ContractModel>): void {
    const { fieldIdentifier, extractedValue, resolvedId, resolvedIds } = field;

    switch (fieldIdentifier) {
        case ContractStandardField.Name:
            contract.name = extractedValue;
            break;
        case ContractStandardField.Description:
            contract.description = extractedValue;
            break;
        case ContractStandardField.ReferenceNumber:
            contract.referenceNumber = extractedValue;
            break;
        case ContractStandardField.Note:
            contract.note = extractedValue;
            break;
        case ContractStandardField.ContractType:
            contract.contractTypeId = resolvedId ?? undefined;
            break;
        case ContractStandardField.StartDate:
            contract.startDate = parseDate(extractedValue);
            break;
        case ContractStandardField.EndDate:
            contract.endDate = parseDate(extractedValue);
            break;
        case ContractStandardField.SignatureDate:
            contract.signatureDate = parseDate(extractedValue);
            break;
        case ContractStandardField.NotificationOfContractExpiry:
            contract.contractExpiryNotificationInDays = parseNumber(extractedValue) as number | undefined;
            break;
        case ContractStandardField.Currency:
            contract.currencyCode = extractedValue;
            break;
        case ContractStandardField.TotalAmount:
            contract.totalAmount = parseNumber(extractedValue);
            break;
        case ContractStandardField.FinalisedAmount:
            contract.finalizedAmount = parseNumber(extractedValue);
            break;
        case ContractStandardField.PaymentTerm:
            contract.paymentTermId = resolvedId ?? undefined;
            break;
        case ContractStandardField.BusinessAreas:
            contract.businessAreas = resolvedIds ?? [];
            break;
        case ContractStandardField.BusinessAreaAccess:
            contract.contractAccessBusinessAreas = resolvedIds ?? [];
            break;
        case ContractStandardField.LegalEntities:
            contract.groupEntities = resolvedIds ?? [];
            break;
        case ContractStandardField.ContractManager:
            contract.contractManagerId = resolvedId ?? undefined;
            break;
        case ContractStandardField.AdditionalUserAccess:
            contract.additionalAccessUsers = resolvedIds ?? [];
            break;
        case ContractStandardField.ExcludedUsers:
            contract.excludedUsers = resolvedIds ?? [];
            break;
        case ContractStandardField.VisibleToAllUsers:
            contract.visibleToAllUsers = parseBoolean(extractedValue);
            break;
        case ContractStandardField.SpecialTerms:
            contract.specialTerms = extractedValue;
            break;
        case ContractStandardField.Tags:
        case ContractStandardField.Documents:
        case ContractStandardField.CounterParties:
        case ContractStandardField.Status:
            break;
        default:
            exhaustiveGuard(fieldIdentifier);
    }
}

/**
 * The template arrives without the fields the user's role hides and with its read-only fields disabled, so the
 * fields left enabled are exactly what a manual create could set.
 */
export function getEditableStandardFields(templateFields: Array<EntityTemplateField>): Set<ContractStandardField> {
    return getReviewableStandardFields(templateFields.filter((field) => !field.disabled));
}

/** Including a party the AI found no legal entity for creates one, which only a user allowed to create one may do. */
export function isProposableParty(party: Pick<ExtractedContractParty, "matchedLegalEntityId">, canCreateLegalEntities: boolean): boolean {
    return canCreateLegalEntities || !!party.matchedLegalEntityId;
}

/** The AI may set nothing a manual create could not. */
export function keepEditableExtraction(
    extraction: ContractExtractionResponse,
    templateFields: Array<EntityTemplateField>,
    canCreateLegalEntities: boolean
): ContractExtractionResponse {
    const editableStandardFields = getEditableStandardFields(templateFields);
    const editableCustomFieldIds = new Set(templateFields.filter((field) => !field.disabled).map((field) => field.customInputFieldId));

    const contractParties = editableStandardFields.has(ContractStandardField.CounterParties)
        ? extraction.contractParties.filter((party) => isProposableParty(party, canCreateLegalEntities))
        : [];

    return {
        ...extraction,
        standardFields: extraction.standardFields.filter((field) => editableStandardFields.has(field.fieldIdentifier)),
        customFields: extraction.customFields.filter((field) => editableCustomFieldIds.has(field.customInputFieldId)),
        contractParties,
    };
}

export function mapCustomFieldToCustomFieldValue(field: ExtractedCustomField): CustomFieldValue {
    let value: any = field.extractedValue;

    if (field.resolvedOptionId) {
        value = field.resolvedOptionId;
    } else if (field.resolvedOptionIds && field.resolvedOptionIds.length > 0) {
        value = field.resolvedOptionIds;
    } else if (field.fieldType === InputFieldTypeEnum.MultiDropDown) {
        value = [];
    } else if (field.fieldType === InputFieldTypeEnum.SingleDropDown) {
        // Extracted text that matched no option must not be sent as-is: the backend expects an option GUID and rejects free text
        value = null;
    }

    return {
        customInputFieldId: field.customInputFieldId,
        value,
    };
}

// The template's defaults stand for every field the document does not answer
export function extractionToContractModel(
    extraction: ContractExtractionResponse,
    entityTemplateId: string,
    templateDefaults: Partial<ContractModel>
): Partial<ContractModel> {
    const contract: Partial<ContractModel> = {
        status: ContractStatus.Draft,
        ...templateDefaults,
        entityTemplateId,
    };

    for (const field of extraction.standardFields) {
        mapStandardFieldToContractModel(field, contract);
    }

    for (const customFieldValue of extraction.customFields.map(mapCustomFieldToCustomFieldValue)) {
        applyCustomFieldValue(contract, customFieldValue.customInputFieldId, customFieldValue.value);
    }

    return contract;
}

// The template's parties join the ones the document names, unless the document already matched them
export function toEditableContractParties(extraction: ContractExtractionResponse, defaultLegalEntityIds: Array<string>): Array<EditableContractParty> {
    const extractedParties = extraction.contractParties.map((party) => ({
        id: crypto.randomUUID(),
        name: party.extractedName,
        address: party.extractedAddress,
        city: party.extractedCity,
        zipCode: party.extractedZipCode,
        country: party.extractedCountry,
        registrationNumber: party.extractedRegistrationNumber,
        legalEntityId: party.matchedLegalEntityId,
        citation: party.citation,
    }));
    const defaultParties = defaultLegalEntityIds
        .filter((legalEntityId) => !extraction.contractParties.some((party) => party.matchedLegalEntityId === legalEntityId))
        .map((legalEntityId) => ({ id: crypto.randomUUID(), legalEntityId }));

    return [...extractedParties, ...defaultParties];
}
